import { useRef } from 'react'
import { createConnection } from './connection'
import { useSelector, useDispatch } from 'react-redux';
import { selectSignalRConfig } from 'providers/store'
import { createCallbacks } from './callbacks'
import { connectedFailedAction, setStatusAction } from './store/actions'
import { connectionIdSelector, statusSelector } from './store/selectors'
import { connectionErrorSelector } from './store/selectors'
import { Toast } from 'primereact/toast'

// ReSharper disable once InconsistentNaming
const SignalR = function(props) {

    /* Toast ref */
    const toastRef = useRef(null)
    const connectionRef = useRef(null)

    /* Dispatch */
    const dispatch = useDispatch()
    const callbacks = createCallbacks(dispatch, toastRef)
    const setStatus = (statum) => dispatch(setStatusAction(statum))

    /* Selectors */
    const config = useSelector(selectSignalRConfig)
    const connectionId = useSelector(connectionIdSelector)
    const status = useSelector(statusSelector)
    const connectionError = useSelector(connectionErrorSelector)

    const connected = status === 'Connected'

    if (config && config.url && status === 'Idle' && !connectionRef.current) {
        const connection = createConnection(config.url, callbacks)
        connectionRef.current = connection

        const startWithRetry = () => {
            connection.start()
                .then(() => setStatus(connection.state))
                .catch(err => {
                    console.error('SignalR start failed, retrying in 30s', err)
                    dispatch(connectedFailedAction(err))
                    setTimeout(startWithRetry, 30_000)
                })
            setStatus(connection.state)
        }

        connection.onclose(() => {
            // automaticReconnect exhausted (or connection explicitly closed). Re-arm.
            setStatus(connection.state)
            setTimeout(startWithRetry, 5_000)
        })
        connection.onreconnecting(() => setStatus(connection.state))
        connection.onreconnected(() => setStatus(connection.state))

        // When the tab is backgrounded or the machine sleeps, the browser
        // throttles the keep-alive timer and the OS may kill the socket. On
        // wake, kick a fresh start if we've fully dropped instead of waiting on
        // throttled reconnect timers. accessTokenFactory refreshes the token.
        const resumeIfDropped = () => {
            if (document.visibilityState !== 'visible') return
            if (connection.state === 'Disconnected') {
                startWithRetry()
            }
        }
        document.addEventListener('visibilitychange', resumeIfDropped)
        window.addEventListener('online', resumeIfDropped)

        startWithRetry()
    }

    return <div id={props.id} state={status}>
               <Toast ref={toastRef} />
               {props.children}
           </div>
}

export default SignalR
